import { jobsApi, type HangfireState } from '@/api/queries/jobs';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Button, Icon, Modal, StatusPill, useToast, type StatusTone } from '@/components/ui';
import { formatDateTime } from '@/utils/formatDate';

function stateTone(state: string): StatusTone {
  if (state === 'Succeeded') return 'active';
  if (state === 'Failed' || state === 'Deleted') return 'dnc';
  return 'inactive';
}

// Well-known Hangfire state-data keys rendered specially; the rest of the bag is
// shown generically (keys vary by storage/version).
const EXCEPTION_KEYS = ['ExceptionType', 'ExceptionMessage', 'ExceptionDetails'];

/**
 * Overview detail for one Hangfire job (admin monitoring "Recent jobs" rows):
 * metadata, full state timeline and — for failed jobs — the exception with
 * stack trace. Requeue/Delete reuse the same mutations as the row actions.
 */
export function JobDetailModal({
  jobId,
  canRequeue,
  onClose,
}: {
  jobId: string;
  canRequeue: boolean;
  onClose: () => void;
}) {
  const { data, isPending } = jobsApi.useJobDetail(jobId);
  const requeue = jobsApi.useRequeueJob();
  const remove = jobsApi.useDeleteJob();
  const toast = useToast();

  const exception = data?.history.find((h) => h.data.ExceptionDetails);

  const onRequeue = async () => {
    try {
      await requeue.mutateAsync(jobId);
      toast.show({ title: 'Job requeued', severity: 'success' });
      onClose();
    } catch (err) {
      toast.show({
        title: 'Failed to requeue',
        description: err instanceof Error ? err.message : undefined,
        severity: 'error',
      });
    }
  };

  const onDelete = async () => {
    try {
      await remove.mutateAsync(jobId);
      toast.show({ title: 'Job deleted', severity: 'success' });
      onClose();
    } catch (err) {
      toast.show({
        title: 'Failed to delete',
        description: err instanceof Error ? err.message : undefined,
        severity: 'error',
      });
    }
  };

  return (
    <Modal open onClose={onClose} title="Job detail" size="lg">
      {isPending || !data ? (
        <LoadingSpinner />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            <Meta label="Id">
              <code style={{ fontSize: 12 }}>{data.id}</code>
            </Meta>
            <Meta label="Job">
              <code style={{ fontSize: 12 }}>{data.job ?? '—'}</code>
            </Meta>
            <Meta label="Created">{formatDateTime(data.createdAt) ?? '—'}</Meta>
          </div>

          <div>
            <h3 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600, color: 'var(--text-2)' }}>
              State timeline
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {data.history.length === 0 && (
                <span style={{ fontSize: 13, color: 'var(--text-3)' }}>No state history.</span>
              )}
              {data.history.map((h, i) => (
                <StateRow key={i} state={h} />
              ))}
            </div>
          </div>

          {exception && (
            <div>
              <h3 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600, color: 'var(--text-2)' }}>
                Exception
              </h3>
              <pre
                style={{
                  margin: 0,
                  padding: 12,
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--surface-2)',
                  border: '1px solid var(--divider)',
                  fontSize: 11.5,
                  fontFamily: 'var(--font-mono)',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: 280,
                  overflowY: 'auto',
                }}
              >
                {exception.data.ExceptionDetails}
              </pre>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            {canRequeue && (
              <Button
                variant="secondary"
                leftIcon={<Icon name="refresh" size={13} />}
                disabled={requeue.isPending}
                onClick={() => void onRequeue()}
              >
                Requeue
              </Button>
            )}
            <Button
              variant="danger"
              leftIcon={<Icon name="trash" size={13} />}
              disabled={remove.isPending}
              onClick={() => void onDelete()}
            >
              Delete
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function StateRow({ state }: { state: HangfireState }) {
  // Surface non-exception data entries inline (e.g. PerformanceDuration, Latency);
  // the stack trace gets its own block below the timeline.
  const extras = Object.entries(state.data).filter(([k]) => !EXCEPTION_KEYS.includes(k));
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: 10,
        borderRadius: 8,
        background: 'var(--surface-1)',
        border: '1px solid var(--border)',
      }}
    >
      <StatusPill status={stateTone(state.stateName)}>{state.stateName}</StatusPill>
      <div style={{ minWidth: 0, flex: 1 }}>
        {state.reason && <div style={{ fontSize: 12.5, color: 'var(--text-2)' }}>{state.reason}</div>}
        {state.data.ExceptionMessage && (
          <div style={{ fontSize: 12, color: 'var(--red, var(--text-2))' }}>
            {state.data.ExceptionType ? `${state.data.ExceptionType}: ` : ''}
            {state.data.ExceptionMessage}
          </div>
        )}
        {extras.length > 0 && (
          <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 2 }}>
            {extras.map(([k, v]) => `${k}: ${v}`).join(' · ')}
          </div>
        )}
      </div>
      <span style={{ fontSize: 11.5, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
        {formatDateTime(state.createdAt) ?? ''}
      </span>
    </div>
  );
}

function Meta({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 120 }}>
      <span style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </span>
      <span style={{ fontSize: 13, color: 'var(--text-1)' }}>{children}</span>
    </div>
  );
}
